/**
 * Layaway and hold. §6.7 `POS-007`; §17 `OPS-105`; Appendix C.1.
 *
 * > Layaway / hold: an order can be paid over time **before manufacture begins**, with a
 * > configurable cancellation and forfeit policy.
 *
 * Two separate things, and only the first is a gate.
 *
 * **"Before manufacture begins" is the whole point of layaway.** A shop lets a customer pay for
 * glasses in instalments precisely because nothing has been spent yet: no lens has been ordered, no
 * frame has left the shelf. The moment a job is created the shop is committed to a cost it cannot
 * recover if the customer stops paying, and a lens ground to one prescription has no second buyer.
 * So the gate is on job creation, not on collection, and it is the reason layaway is safe to offer
 * at all.
 *
 * **The forfeit policy is not a gate and must not become one.** `OPS-105` already settles what
 * happens to the money on cancellation: a retained fee, explicitly decided, with ledger entries to
 * match. What `POS-007` adds is that the retained fee can come from a configured policy rather than
 * being typed in each time. It ships unset, like every other number the PRD leaves blank.
 *
 * Note which way "unset" falls here, because it is the opposite of `POS-006`'s and for the same
 * reason. An unconfigured return window makes returning need a person, and an unconfigured forfeit
 * policy makes keeping the customer's money need a person. The principle underneath both: **when
 * the spec configures no number, the action that moves money against somebody's interest is the one
 * that requires an explicit human decision.**
 */
export class LayawayError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'LayawayError';
    }
}
/**
 * Whether the lab order may be raised.
 *
 * A plain order manufactures on confirmation: `POS-002` describes the market's normal shape as "50%
 * at order, balance at collection", so waiting for full payment would stop every ordinary sale. A
 * layaway order is the deliberate exception and waits for the last rupee.
 */
export function canBeginManufacture(request) {
    if (!request.isLayaway)
        return { allowed: true };
    if (request.outstandingMinor <= 0n)
        return { allowed: true };
    return {
        allowed: false,
        code: 'LAYAWAY_NOT_PAID',
        message: `This order is on layaway with ${String(request.outstandingMinor)} still owing, and POS-007 ` +
            `holds manufacture until it is paid. Take the balance, or cancel the layaway, before raising ` +
            `the job.`,
    };
}
/**
 * `POS-007`'s "configurable ... forfeit policy", applied to what has actually been paid.
 *
 * Returns a **proposal**. Nothing here decides that the shop keeps the money: `OPS-105` requires the
 * cancellation to carry a reason and a refund decision, and `sale_void` is the permission that
 * makes it somebody's. This turns a configured percentage into an amount so the person deciding is
 * shown a number rather than asked to compute one.
 *
 * Refuses rather than defaulting when no policy is configured. A forfeit invented by the software
 * is the shop keeping a customer's money on nobody's authority, which is the one outcome worth
 * making impossible.
 */
export function proposeForfeit(input) {
    if (input.paidMinor < 0n) {
        throw new LayawayError('PAID_NEGATIVE', 'A layaway cannot have had less than nothing paid into it.');
    }
    if (input.policyPercent === undefined) {
        throw new LayawayError('FORFEIT_POLICY_UNSET', 'No cancellation and forfeit policy is configured, and POS-007 names no percentage to fall ' +
            'back on. Set one, or decide the retained fee explicitly on the void (OPS-105).');
    }
    const percent = Number(input.policyPercent);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
        throw new LayawayError('FORFEIT_POLICY_INVALID', `A forfeit policy of ${input.policyPercent} is not a percentage between 0 and 100.`);
    }
    // Integer arithmetic throughout, in minor units, per Appendix D `MR-01`. The percentage is
    // scaled by 100 so a policy like 12.5% is exact rather than a float.
    const scaled = BigInt(Math.round(percent * 100));
    const retainedMinor = (input.paidMinor * scaled) / 10000n;
    return {
        retainedMinor,
        returnedMinor: input.paidMinor - retainedMinor,
        percent: input.policyPercent,
    };
}
//# sourceMappingURL=layaway.js.map